import React, { useEffect, useState, useMemo } from "react";
import styled, { css } from "styled-components";
import axios from "axios";
import { useLanguage } from "./LanguageContext";
import { useLocation, useNavigate } from "react-router-dom";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { api } from "../lib/http";

const API_BASE_URL = "https://shopaholics.ge/api";

/* ============ THEME ============ */
const COLORS = {
  text: "#111827",
  dim: "#6b7280",
  line: "rgba(0,0,0,.08)",
  bg: "#ffffff",
  brand: "#a15a5a",
  brandDark: "#8e4a4a",
  subtle: "#f7f7f8",
  shadow: "0 20px 60px rgba(0,0,0,.18)",
};

/* helpers */
const mobile = (styles) => css`
  @media (max-width: 420px) {
    ${styles}
  }
`;

/* ============ STYLES ============ */
const Page = styled.div`
  background: linear-gradient(180deg, #fafafb 0%, #f5f6f8 100%);
  display: grid;
  align-items: start;
  justify-items: center;
  padding: clamp(16px, 3vw, 32px);
`;

const Card = styled.div`
  width: min(460px, 92vw);
  max-width: 560px;
  background: ${COLORS.bg};
  border: 1px solid ${COLORS.line};
  border-radius: 16px;
  box-shadow: ${COLORS.shadow};
  padding: clamp(16px, 3vw, 24px);
  overflow: hidden; /* არასდროს გადმოიჭრას შიგთავსი */
  ${mobile`
    width: calc(100vw - 60px);
    padding: 14px;
    border-radius: 14px;
  `}
`;

const Title = styled.h1`
  margin: 0 0 6px;
  font-size: 20px;
  font-weight: 900;
  color: ${COLORS.text};
  ${mobile`font-size: 18px;`}
`;

const Subtitle = styled.p`
  margin: 0 0 16px;
  color: ${COLORS.dim};
  font-size: 14px;
  ${mobile`font-size: 13px;`}
`;

const Form = styled.form`
  display: grid;
  gap: 12px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 800;
  color: ${COLORS.text};
  ${mobile`font-size: 12px;`}
`;

const Field = styled.div`
  position: relative;
  min-width: 0; /* grid/flex child overflow fix */
`;

const Input = styled.input`
  box-sizing: border-box;         /* მთავარი ფიქსი */
  width: 100%;
  height: 48px;
  padding: 0 44px 0 12px;
  border: 1px solid ${COLORS.line};
  border-radius: 12px;
  background: #fff;
  font-size: 14px;
  color: ${COLORS.text};
  outline: none;
  transition: border-color .15s ease, box-shadow .15s ease;
  -webkit-tap-highlight-color: transparent;
  &:focus { border-color: ${COLORS.brand}; box-shadow: 0 0 0 3px rgba(161,90,90,.15); }
  ${mobile`
    height: 46px;
    font-size: 16px; /* iOS-ზე zoom-ის თავიდან აცილება */
    padding-right: 42px;
  `}
`;

const Toggle = styled.button`
  position: absolute;
  top: 50%;
  right: 6px;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  border: 0;
  background: transparent;
  display: grid;
  place-items: center;
  cursor: pointer;
  color: ${COLORS.dim};
  border-radius: 8px;
  ${mobile`right: 4px; width: 34px; height: 34px;`}
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  ${mobile`grid-template-columns: 1fr;`}
`;

const Button = styled.button`
  height: 48px;
  border-radius: 12px;
  border: 0;
  background: ${COLORS.text};
  color: #fff;
  font-weight: 800;
  font-size: 14px;
  cursor: pointer;
  transition: transform .15s ease, filter .15s ease, opacity .15s ease;
  &:hover { transform: translateY(-1px); filter: brightness(.96); }
  &:disabled { opacity: .6; cursor: not-allowed; transform: none; }
  ${mobile`height: 46px; font-size: 15px;`}
`;

const Ghost = styled(Button)`
  background: #fff;
  color: ${COLORS.text};
  border: 1px solid ${COLORS.line};
`;

const Msg = styled.div`
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.35;
  ${mobile`font-size: 12.5px;`}
  ${(p) =>
    p.$type === "error"
      ? `background: #fff3f3; color: ${COLORS.brandDark}; border: 1px solid #ffd7d7;`
      : `background: #effaf1; color: #065f46; border: 1px solid #d1fae5;`}
`;

/* ============ UTIL ============ */
const safeParse = (s) => {
  try { return JSON.parse(s); } catch { return null; }
};

/* ============ COMPONENT ============ */
const ChangePassword = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  // user from Link state or localStorage fallback
  const userFromState = location.state?.user || null;
  const userFromStorage = useMemo(() => safeParse(localStorage.getItem("user") || "null"), []);
  const user = userFromState || userFromStorage;

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // top + clear any leftover scroll-lock
  useEffect(() => {
    document.body.style.overflow = "";
    document.body.style.position = "";
    document.body.style.width = "";
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.documentElement.scrollTop = 0;
  }, []);

  if (!user) {
    return (
      <Page>
        <Card>
          <Title>{language === "en" ? "Unauthorized access" : "არასანქცირებული წვდომა"}</Title>
          <Subtitle>
            {language === "en"
              ? "Please log in and try again."
              : "გთხოვთ შეხვიდეთ სისტემაში და სცადოთ თავიდან."}
          </Subtitle>
          <Row>
            <Ghost onClick={() => navigate("/")}>
              {language === "en" ? "Back to Home" : "დაბრუნება მთავარზე"}
            </Ghost>
          </Row>
        </Card>
      </Page>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setError(null);
    setSuccess(null);

    if (newPassword !== confirmPassword) {
      setError(language === "en" ? "New passwords do not match." : "ახალი პაროლები არ ემთხვევა.");
      return;
    }

    const strong = /^(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
    if (!strong.test(newPassword)) {
      setError(
        language === "en"
          ? "Password must be 8+ chars and include uppercase, number and symbol."
          : "პაროლი უნდა იყოს მინ. 8 სიმბოლო, შეიცავდეს დიდ ასოს, ციფრს და სიმბოლოს."
      );
      return;
    }

    try {
      setSubmitting(true);
      const { data } = await api.post(
        `${API_BASE_URL}/change_password.php`,
        { email: user.email, oldPassword, newPassword },
        { headers: { "Content-Type": "application/json" }, withCredentials: true, validateStatus: () => true }
      );

      if (data?.success) {
        setSuccess(language === "en" ? "Password changed successfully!" : "პაროლი წარმატებით შეიცვალა!");
        setOldPassword(""); setNewPassword(""); setConfirmPassword("");
        setTimeout(() => navigate("/"), 1500);
      } else {
        setError(
          data?.error ||
            (language === "en" ? "Failed to change password." : "პაროლის შეცვლა ვერ მოხერხდა.")
        );
      }
    } catch {
      setError(language === "en" ? "Something went wrong. Please try again." : "დაფიქსირდა შეცდომა, გთხოვთ სცადოთ თავიდან.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page>
      <Card>
        <Title>{language === "en" ? "Change Password" : "პაროლის შეცვლა"}</Title>
        <Subtitle>
          {language === "en"
            ? "For your security, please enter your current password first."
            : "უსაფრთხოების მიზნით, ჯერ შეიყვანეთ თქვენი ამჟამინდელი პაროლი."}
        </Subtitle>

        {error && <Msg $type="error">{error}</Msg>}
        {success && <Msg $type="success">{success}</Msg>}

        <Form onSubmit={handleSubmit}>
          <div>
            <Label>{language === "en" ? "Current Password" : "ამჟამინდელი პაროლი"}</Label>
            <Field>
              <Input
                type={showOld ? "text" : "password"}
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                required
                autoComplete="current-password"
                inputMode="text"
              />
              <Toggle type="button" onClick={() => setShowOld((s) => !s)} aria-label="Toggle password">
                {showOld ? <AiOutlineEyeInvisible size={20} /> : <AiOutlineEye size={20} />}
              </Toggle>
            </Field>
          </div>

          <div>
            <Label>{language === "en" ? "New Password" : "ახალი პაროლი"}</Label>
            <Field>
              <Input
                type={showNew ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoComplete="new-password"
                inputMode="text"
                placeholder={
                  language === "en"
                    ? "8+ chars, uppercase, number, symbol"
                    : "8+ სიმბოლო, დიდი ასო, ციფრი, სიმბოლო"
                }
              />
              <Toggle type="button" onClick={() => setShowNew((s) => !s)} aria-label="Toggle password">
                {showNew ? <AiOutlineEyeInvisible size={20} /> : <AiOutlineEye size={20} />}
              </Toggle>
            </Field>
          </div>

          <div>
            <Label>{language === "en" ? "Confirm New Password" : "გაიმეორეთ ახალი პაროლი"}</Label>
            <Field>
              <Input
                type={showConfirm ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                inputMode="text"
              />
              <Toggle type="button" onClick={() => setShowConfirm((s) => !s)} aria-label="Toggle password">
                {showConfirm ? <AiOutlineEyeInvisible size={20} /> : <AiOutlineEye size={20} />}
              </Toggle>
            </Field>
          </div>

          <Row>
            <Button type="submit" disabled={submitting}>
              {submitting
                ? language === "en" ? "Processing..." : "მიმდინარეობს..."
                : language === "en" ? "Change Password" : "პაროლის შეცვლა"}
            </Button>
            <Ghost type="button" onClick={() => navigate(-1)}>
              {language === "en" ? "Back" : "დაბრუნება"}
            </Ghost>
          </Row>
        </Form>
      </Card>
    </Page>
  );
};

export default ChangePassword;
